import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Lock } from 'lucide-react';
import { PRICING_RULES, PRICING_STATUS, computePricing, itemMarginPct } from '@adisys/shared';
import { api } from '../../../lib/api';
import { useAuth } from '../../../lib/auth';
import { MetaBadge, Notice, Section, errorText, inr2, qtyLabel, type TabProps } from '../../../lib/crm';
import { Button, Card, EmptyState, ErrorState, Field, Input, Select, Skeleton, Textarea, useToast } from '../../../components/ui';

interface EditLine { boqItemId: string; unitCost: number; unitPrice: number; gstRate: number }

const num = (v: string) => (v === '' ? 0 : Number(v));

export function PricingTab({ project, reload }: TabProps) {
  const { can } = useAuth();
  const toast = useToast();
  const boqId: string | undefined = project.summary?.boq?.id;

  const q = useQuery({
    queryKey: ['crm', 'project', project.id, 'pricing', boqId],
    queryFn: () => api.get(`/crm/boqs/${boqId}/pricing`),
    enabled: Boolean(boqId),
  });
  const data = q.data;
  const full = Boolean(data?.lines?.length && data.lines[0].unitCost !== undefined);

  const [lines, setLines] = useState<EditLine[]>([]);
  const [instAmt, setInstAmt] = useState(0); const [instCost, setInstCost] = useState(0); const [instGst, setInstGst] = useState(18);
  const [svcAmt, setSvcAmt] = useState(0); const [svcCost, setSvcCost] = useState(0); const [svcGst, setSvcGst] = useState(18);
  const [mode, setMode] = useState<'percent' | 'amount'>('percent');
  const [disc, setDisc] = useState(0);
  const [notes, setNotes] = useState('');
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!data || !full) return;
    setLines(data.lines.map((l: any) => ({ boqItemId: l.boqItemId, unitCost: Number(l.unitCost), unitPrice: Number(l.unitPrice), gstRate: Number(l.gstRate) })));
    setInstAmt(Number(data.installationAmount)); setInstGst(Number(data.installationGst));
    setSvcAmt(Number(data.serviceAmount)); setSvcGst(Number(data.serviceGst));
    setInstCost(Number(data.installationCost ?? 0)); setSvcCost(Number(data.serviceCost ?? 0));
    setMode(data.discountMode); setDisc(Number(data.discountValue)); setNotes(data.notes ?? ''); setDirty(false);
  }, [data, full]);

  const editable = full && data.status === 'draft' && can('crm.pricing.manage') && project.status === 'active' && project.stage === 'pricing';

  const live = useMemo(() => {
    if (!full) return null;
    return computePricing({
      lines: data.lines.map((l: any, i: number) => ({ quantity: Number(l.quantity), unitCost: lines[i]?.unitCost ?? 0, unitPrice: lines[i]?.unitPrice ?? 0, gstRate: lines[i]?.gstRate ?? 18 })),
      installation: { amount: instAmt, cost: instCost, gstRate: instGst },
      service: { amount: svcAmt, cost: svcCost, gstRate: svcGst },
      discount: { mode, value: disc },
    });
  }, [data, full, lines, instAmt, instCost, instGst, svcAmt, svcCost, svcGst, mode, disc]);

  const body = () => ({
    lines, installationAmount: instAmt, installationCost: instCost, installationGst: instGst,
    serviceAmount: svcAmt, serviceCost: svcCost, serviceGst: svcGst, discountMode: mode, discountValue: disc, notes: notes || null,
  });
  const save = useMutation({
    mutationFn: () => api.put(`/crm/pricings/${data.id}`, body()),
    onSuccess: () => { toast.success('Pricing saved'); setDirty(false); void q.refetch(); },
    onError: (e) => toast.error('Could not save the pricing', errorText(e)),
  });
  const approve = useMutation({
    mutationFn: async () => { if (dirty) await api.put(`/crm/pricings/${data.id}`, body()); return api.post(`/crm/pricings/${data.id}/approve`); },
    onSuccess: () => { toast.success('Pricing approved and released to Sales'); void q.refetch(); reload(); },
    onError: (e: any) => toast.error(e?.status === 403 ? 'Management approval needed' : 'Could not approve', errorText(e)),
  });
  const escalate = useMutation({
    mutationFn: async () => { if (dirty) await api.put(`/crm/pricings/${data.id}`, body()); return api.post(`/crm/pricings/${data.id}/escalate`); },
    onSuccess: (r: any) => { toast.success('Management notified', `${r.notified} approver(s) alerted.`); setDirty(false); void q.refetch(); },
    onError: (e) => toast.error('Could not request approval', errorText(e)),
  });

  const touch = <T,>(set: (v: T) => void) => (v: T) => { set(v); setDirty(true); };

  if (!boqId) return <EmptyState icon={<Lock className="h-5 w-5" />} title="Nothing to price yet" description="Pricing starts when the technical team submits an approved BOQ." />;
  if (q.isLoading) return <Skeleton className="h-48 w-full" />;
  if (q.error) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  if (!data?.exists) return <EmptyState icon={<Lock className="h-5 w-5" />} title="Pricing has not started" description="It opens as soon as the BOQ is approved and submitted for pricing." />;

  if (data.hidden) {
    return <EmptyState icon={<Lock className="h-5 w-5" />} title="Pricing is confidential until approved"
      description="Costs and margins are held by the pricing manager. The prices appear here once pricing is approved and released to Sales." />;
  }

  // Customer-level view: approved prices, no cost, no margin.
  if (!full) {
    const t = data.totals;
    return (
      <div className="space-y-4">
        <Notice tone="success">Pricing approved and released to Sales.</Notice>
        <Card padded={false} className="overflow-x-auto">
          <table className="w-full min-w-[620px] text-left text-sm">
            <thead><tr className="border-b border-line bg-sunken/60 text-ink-600">
              {['#', 'Item', 'Qty', 'Rate', 'GST %', 'Taxable', 'Total'].map((h, i) => <th key={h} className={`px-3 py-2.5 text-sm font-bold ${i > 1 ? 'text-right' : ''}`}>{h}</th>)}</tr></thead>
            <tbody className="divide-y divide-line">
              {data.lines.map((l: any) => (
                <tr key={l.boqItemId}><td className="px-3 py-2.5 text-ink-500">{l.lineNo}</td>
                  <td className="px-3 py-2.5"><p className="font-medium text-ink-900">{l.productName}</p><p className="text-xs text-ink-500">{[l.brandName, l.modelNumber].filter(Boolean).join(' · ')}</p></td>
                  <td className="tabular px-3 py-2.5 text-right">{qtyLabel(l.quantity)} {l.unit}</td>
                  <td className="tabular px-3 py-2.5 text-right">{inr2(l.unitPrice)}</td><td className="tabular px-3 py-2.5 text-right">{l.gstRate}%</td>
                  <td className="tabular px-3 py-2.5 text-right">{inr2(l.taxable)}</td><td className="tabular px-3 py-2.5 text-right">{inr2(l.total)}</td></tr>
              ))}
            </tbody>
          </table>
        </Card>
        <Totals rows={[['Goods subtotal', t.goodsSubtotal], ['Discount', -t.discountAmount], ['Installation', data.installationAmount], ['Service', data.serviceAmount],
          ['Taxable value', t.taxableTotal], ['GST', t.gstTotal]]} grand={t.grandTotal} />
      </div>
    );
  }

  const r = live!;
  const approvedLocked = data.status !== 'draft';
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <MetaBadge meta={PRICING_STATUS} value={data.status} />
        <span className="text-xs text-ink-500">BOQ v{data.boqVersion}</span>
        <span className="flex items-center gap-1 text-xs text-warning"><Lock className="h-3.5 w-3.5" /> Confidential: cost and margin are visible only to pricing and management</span>
      </div>
      {approvedLocked && <Notice tone={data.status === 'approved' ? 'success' : 'info'}>This pricing is {data.status} and can no longer be changed. Revise the BOQ to reprice.</Notice>}
      {!approvedLocked && !editable && <Notice>You can view this sheet but not edit it{project.stage !== 'pricing' ? ' at this stage' : ''}.</Notice>}

      <Card padded={false} className="overflow-x-auto">
        <table className="w-full min-w-[980px] text-left text-sm">
          <thead><tr className="border-b border-line bg-sunken/60 text-ink-600">
            {['Item', 'Qty', 'Catalogue price', 'Unit cost', 'Unit price', 'GST %', 'Margin', 'Line total'].map((h, i) => <th key={h} className={`px-3 py-2.5 text-sm font-bold ${i > 0 ? 'text-right' : ''}`}>{h}</th>)}</tr></thead>
          <tbody className="divide-y divide-line">
            {data.lines.map((l: any, i: number) => {
              const e = lines[i]; if (!e) return null;
              const set = (patch: Partial<EditLine>) => { setLines((ls) => ls.map((x, k) => (k === i ? { ...x, ...patch } : x))); setDirty(true); };
              const m = itemMarginPct(e.unitCost, e.unitPrice);
              return (
                <tr key={l.boqItemId}>
                  <td className="px-3 py-2"><p className="font-medium text-ink-900">{l.productName}</p><p className="text-xs text-ink-500">{[l.brandName, l.modelNumber].filter(Boolean).join(' · ')}</p></td>
                  <td className="tabular px-3 py-2 text-right">{qtyLabel(l.quantity)} {l.unit}</td>
                  <td className="tabular px-3 py-2 text-right text-ink-500">{inr2(l.catalogPrice)}</td>
                  <td className="px-3 py-2 text-right"><Input type="number" min={0} step="0.01" className="ml-auto w-28 text-right" disabled={!editable} value={e.unitCost} onChange={(ev) => set({ unitCost: num(ev.target.value) })} aria-label="Unit cost" /></td>
                  <td className="px-3 py-2 text-right"><Input type="number" min={0} step="0.01" className="ml-auto w-28 text-right" disabled={!editable} value={e.unitPrice} onChange={(ev) => set({ unitPrice: num(ev.target.value) })} aria-label="Unit price" invalid={e.unitPrice <= 0} /></td>
                  <td className="px-3 py-2 text-right"><Input type="number" min={0} max={40} step="0.5" className="ml-auto w-20 text-right" disabled={!editable} value={e.gstRate} onChange={(ev) => set({ gstRate: num(ev.target.value) })} aria-label="GST rate" /></td>
                  <td className={`tabular px-3 py-2 text-right ${m < PRICING_RULES.minMarginPct ? 'text-danger' : 'text-ink-700'}`}>{m}%</td>
                  <td className="tabular px-3 py-2 text-right font-medium">{inr2(r.lines[i]?.total)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      <div className="grid gap-5 lg:grid-cols-2">
        <Section title="Charges and discount" description="Installation and service are billed as lots; costs are internal.">
          <Card className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-3">
              <Field label="Installation charge"><Input type="number" min={0} disabled={!editable} value={instAmt} onChange={(e) => touch(setInstAmt)(num(e.target.value))} /></Field>
              <Field label="Installation cost"><Input type="number" min={0} disabled={!editable} value={instCost} onChange={(e) => touch(setInstCost)(num(e.target.value))} /></Field>
              <Field label="GST %"><Input type="number" min={0} max={40} disabled={!editable} value={instGst} onChange={(e) => touch(setInstGst)(num(e.target.value))} /></Field>
              <Field label="Service charge"><Input type="number" min={0} disabled={!editable} value={svcAmt} onChange={(e) => touch(setSvcAmt)(num(e.target.value))} /></Field>
              <Field label="Service cost"><Input type="number" min={0} disabled={!editable} value={svcCost} onChange={(e) => touch(setSvcCost)(num(e.target.value))} /></Field>
              <Field label="GST %"><Input type="number" min={0} max={40} disabled={!editable} value={svcGst} onChange={(e) => touch(setSvcGst)(num(e.target.value))} /></Field>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Discount type"><Select disabled={!editable} value={mode} onChange={(e) => touch(setMode)(e.target.value as 'percent' | 'amount')}>
                <option value="percent">Percent of goods</option><option value="amount">Fixed amount</option></Select></Field>
              <Field label={mode === 'percent' ? 'Discount %' : 'Discount amount'} hint={`Up to ${PRICING_RULES.maxDiscountPct}% needs no management sign-off`}>
                <Input type="number" min={0} disabled={!editable} value={disc} onChange={(e) => touch(setDisc)(num(e.target.value))} /></Field>
            </div>
            <Field label="Notes"><Textarea rows={2} disabled={!editable} value={notes} onChange={(e) => touch(setNotes)(e.target.value)} /></Field>
          </Card>
        </Section>

        <Section title="Totals">
          <Totals rows={[['Goods subtotal', r.goodsSubtotal], [`Discount (${r.discountPct}%)`, -r.discountAmount], ['Installation', r.installationAmount], ['Service', r.serviceAmount],
            ['Taxable value', r.taxableTotal], ['GST', r.gstTotal]]} grand={r.grandTotal}
            extra={<div className="mt-3 space-y-1 border-t border-line pt-3 text-sm">
              <Row label="Total cost" value={inr2(r.costTotal)} />
              <Row label="Margin" value={inr2(r.marginAmount)} />
              <Row label="Margin %" value={`${r.marginPct}%`} bad={r.breaches.includes('margin')} />
            </div>} />
          {r.breaches.length > 0 && (
            <div className="mt-3 flex gap-2 rounded-lg bg-danger-soft px-3.5 py-2.5 text-sm text-danger">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <p>{r.breaches.includes('discount') && `Discount ${r.discountPct}% exceeds the ${PRICING_RULES.maxDiscountPct}% limit. `}
                {r.breaches.includes('margin') && `Margin ${r.marginPct}% is below the ${PRICING_RULES.minMarginPct}% floor. `}Management must approve this price.</p>
            </div>
          )}
        </Section>
      </div>

      {editable && (
        <div className="flex flex-wrap gap-2">
          <Button disabled={!dirty} loading={save.isPending} onClick={() => save.mutate()}>Save draft</Button>
          {can('crm.pricing.approve') && (
            <Button variant="success" icon={<CheckCircle2 className="h-4 w-4" />} loading={approve.isPending} onClick={() => approve.mutate()}>Approve and release to Sales</Button>
          )}
          {r.breaches.length > 0 && <Button loading={escalate.isPending} onClick={() => escalate.mutate()}>Request management approval</Button>}
        </div>
      )}
    </div>
  );
}

function Row({ label, value, bad }: { label: string; value: string; bad?: boolean }) {
  return <div className="flex justify-between"><span className="text-ink-600">{label}</span><span className={`tabular font-medium ${bad ? 'text-danger' : 'text-ink-900'}`}>{value}</span></div>;
}

function Totals({ rows, grand, extra }: { rows: Array<[string, number]>; grand: number; extra?: React.ReactNode }) {
  return (
    <Card>
      <div className="space-y-1 text-sm">
        {rows.filter(([, v]) => v !== 0).map(([k, v]) => <Row key={k} label={k} value={inr2(v)} />)}
        <div className="flex justify-between border-t border-line pt-2 text-base font-semibold"><span>Grand total</span><span className="tabular">{inr2(grand)}</span></div>
      </div>
      {extra}
    </Card>
  );
}
